import React, { useState } from 'react';
import { CopyPlusIcon, FlagIcon, LockIcon, PlusIcon, TimerIcon } from 'lucide-react';
import { EncabezadoPagina } from '../components/layout/AppLayout';
import { EncabezadoPanel, EstadoVacio, Panel } from '../components/ui/Basicos';
import { TarjetaEtapa } from '../components/flujos/TarjetaEtapa';
import { EditorEtapa } from '../components/flujos/EditorEtapa';
import { useDatos } from '../contexts/DatosContext';
import { MATERIAS } from '../data/catalogos';
import { estiloColor } from '../data/flujos';
import type { EtapaFlujo, Materia } from '../types';
function etapaVacia(): EtapaFlujo {
  return {
    id: `et-${Date.now()}`,
    nombre: '',
    descripcion: '',
    tienePlazo: false,
    diasSugeridos: 10,
    documentosEsperados: [],
    color: 'marino',
    etiqueta: '',
    esHito: false
  };
}
export function FlujosProceso() {
  const {
    rol,
    flujos,
    agregarEtapa,
    actualizarEtapa,
    eliminarEtapa,
    duplicarEtapa,
    reordenarEtapas,
    duplicarFlujo,
    tiposParaMateria
  } = useDatos();
  const [materia, setMateria] = useState<Materia>('Penal');
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [nueva, setNueva] = useState<EtapaFlujo | null>(null);
  const [arrastrando, setArrastrando] = useState<number | null>(null);
  const [destino, setDestino] = useState<number | null>(null);
  const [duplicarHacia, setDuplicarHacia] = useState<Materia | ''>('');
  const [aviso, setAviso] = useState('');
  if (rol !== 'Administrador') {
    return <>
        <EncabezadoPagina titulo="Flujos de proceso" descripcion="Configuración del despacho" data-id="element-1506" data-mp="1vpa1caxur6bp" />
        <Panel data-id="element-1507" data-mp="20qfug9vfcvlr">
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center" data-id="element-1508" data-mp="20qpoc8jlbmwq">
            <LockIcon className="h-6 w-6 text-gris-300" aria-hidden="true" data-id="element-1509" data-mp="24qzrldfzzz7w" />
            <p className="text-sm font-semibold text-gris-700" data-id="element-1510" data-mp="6cygnektnd3x">Módulo restringido</p>
            <p className="max-w-md text-xs text-gris-500" data-id="element-1511" data-mp="acyvh0bu1qbi">
              Solo el rol Administrador puede configurar los flujos de etapas. Cambia de rol en la
              barra superior si necesitas revisarlos.
            </p>
          </div>
        </Panel>
      </>;
  }
  const etapas = flujos[materia];
  const guardarEtapa = (etapa: EtapaFlujo, nuevoOrden: number, esNueva: boolean) => {
    if (esNueva) {
      agregarEtapa(materia, etapa);
      if (nuevoOrden !== etapas.length) reordenarEtapas(materia, etapas.length, nuevoOrden);
      setNueva(null);
      setAviso(`Etapa «${etapa.nombre}» agregada al flujo ${materia}.`);
    } else {
      actualizarEtapa(materia, etapa);
      const actual = etapas.findIndex(e => e.id === etapa.id);
      if (nuevoOrden !== actual) reordenarEtapas(materia, actual, nuevoOrden);
      setEditandoId(null);
      setAviso(`Etapa «${etapa.nombre}» actualizada.`);
    }
  };
  const soltar = (indice: number) => {
    if (arrastrando !== null && arrastrando !== indice) {
      reordenarEtapas(materia, arrastrando, indice);
      setAviso('Orden del flujo actualizado.');
    }
    setArrastrando(null);
    setDestino(null);
  };
  return <>
      <EncabezadoPagina titulo="Flujos de proceso" descripcion="Define las etapas por las que avanza un expediente según su materia. Todo es configurable: nombres, orden, plazos, documentos esperados e hitos." acciones={<button type="button" onClick={() => {
      setNueva(etapaVacia());
      setEditandoId(null);
    }} className="inline-flex h-9 items-center gap-2 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-1513" data-mp="2ozkckasx8x37">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1514" data-mp="26amdcyira91k" />
            Agregar etapa
          </button>} data-id="element-1512" data-mp="2z035dv2grnx9" />

      <div className="grid gap-4 xl:grid-cols-[16rem_1fr]" data-id="element-1515" data-mp="1sc5xoi2x187a">
        <div className="space-y-4" data-id="element-1516" data-mp="1xa7rny1vqrxj">
          <Panel data-id="element-1517" data-mp="1x9644u3ogcyx">
            <EncabezadoPanel titulo="Materias" descripcion="Cada materia tiene su propio flujo" data-id="element-1518" data-mp="1x9o9cg8bi1iz" />
            <ul className="divide-y divide-gris-100" data-id="element-1519" data-mp="1p86xh6xivw23">
              {MATERIAS.map(m => {
              const lista = flujos[m];
              const activa = m === materia;
              return <li key={m} data-id="element-1520" data-mp="1fi5id4n5dnzb">
                    <button type="button" onClick={() => {
                  setMateria(m);
                  setEditandoId(null);
                  setNueva(null);
                  setAviso('');
                }} className={`flex w-full items-center justify-between gap-2 px-5 py-2.5 text-left transition-colors duration-150 ${activa ? 'bg-marino-50' : 'hover:bg-gris-50'}`} data-id="element-1521" data-mp="1fhcag0cgcsfl">
                      <span data-id="element-1522" data-mp="2qxu4jtskuiz2">
                        <span className={`block text-sm ${activa ? 'font-semibold text-marino-800' : 'font-medium text-gris-700'}`} data-id="element-1523" data-mp="2qy2htmsm3qas">
                          {m}
                        </span>
                        <span className="tabular block text-2xs text-gris-400" data-id="element-1524" data-mp="dmrdbh6if4es">
                          {lista.length} etapas · {lista.filter(e => e.esHito).length} hitos
                        </span>
                      </span>
                      <span className="flex gap-0.5" aria-hidden="true" data-id="element-1525" data-mp="3lw12r9ngn6t7">
                        {lista.slice(0, 6).map(e => <span key={e.id} className={`h-4 w-1 rounded-sm ${estiloColor(e.color).punto}`} data-id="element-1526" data-mp="3qw5igxgjnxid" />)}
                      </span>
                    </button>
                  </li>;
            })}
            </ul>
          </Panel>

          <Panel data-id="element-1527" data-mp="1f5l25qrek51u">
            <EncabezadoPanel titulo="Duplicar flujo completo" descripcion={`Copia las ${etapas.length} etapas de ${materia} a otra materia`} data-id="element-1528" data-mp="1f51tz1rk0v10" />
            <div className="space-y-3 px-5 py-4" data-id="element-1529" data-mp="3774jfstd4l5d">
              <label className="block" data-id="element-1530" data-mp="376cpsnjbsxhq">
                <span className="text-2xs font-semibold uppercase tracking-wide text-gris-500" data-id="element-1531" data-mp="3l6vsdf50vbci">
                  Materia destino
                </span>
                <select value={duplicarHacia} onChange={e => setDuplicarHacia(e.target.value as Materia)} className="mt-1.5 h-9 w-full rounded border border-gris-200 px-2.5 text-sm text-gris-700" data-id="element-1532" data-mp="3g9402k0gtn7s">
                  <option value="" data-id="element-1533" data-mp="328jkdwpuk7ii">Selecciona</option>
                  {MATERIAS.filter(m => m !== materia).map(m => <option key={m} value={m} data-id="element-1534" data-mp="2xgjdfkbd0ecf">
                      {m} ({flujos[m].length} etapas)
                    </option>)}
                </select>
              </label>
              <p className="text-2xs text-gris-400" data-id="element-1535" data-mp="24rujq6cmvbap">
                El flujo destino se reemplaza por completo. Después puedes ajustar sus etapas.
              </p>
              <button type="button" disabled={!duplicarHacia} onClick={() => {
              if (!duplicarHacia) return;
              duplicarFlujo(materia, duplicarHacia as Materia);
              setAviso(`Flujo de ${materia} copiado a ${duplicarHacia}.`);
              setDuplicarHacia('');
            }} className="inline-flex h-9 w-full items-center justify-center gap-2 rounded border border-marino-200 bg-marino-50 text-sm font-semibold text-marino-700 transition-colors duration-150 hover:bg-marino-100 disabled:cursor-not-allowed disabled:border-gris-200 disabled:bg-gris-50 disabled:text-gris-400" data-id="element-1536" data-mp="p11sydf3idid">
                <CopyPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1537" data-mp="3eq419u16e4mm" />
                Duplicar flujo
              </button>
            </div>
          </Panel>
        </div>

        <Panel data-id="element-1538" data-mp="3jndr3ash8iaz">
          <EncabezadoPanel titulo={`Flujo de ${materia}`} descripcion="Arrastra las etapas para cambiar su orden. Los hitos generan alerta automática al llegar." accion={<span className="flex items-center gap-3 text-2xs text-gris-500" data-id="element-1540" data-mp="597tsp1xmogh">
                <span className="inline-flex items-center gap-1.5" data-id="element-1541" data-mp="awgu5gcbxu1">
                  <TimerIcon className="h-3.5 w-3.5 text-gris-400" aria-hidden="true" data-id="element-1542" data-mp="cr3bo79fy08" />
                  {etapas.filter(e => e.tienePlazo).length} con plazo
                </span>
                <span className="inline-flex items-center gap-1.5" data-id="element-1543" data-mp="szu0abwag0od">
                  <FlagIcon className="h-3.5 w-3.5 text-ambar-500" aria-hidden="true" data-id="element-1544" data-mp="o7ttq0lpq9it" />
                  {etapas.filter(e => e.esHito).length} hitos
                </span>
              </span>} data-id="element-1539" data-mp="ea5zrcvxl61p" />

          {aviso && <p role="status" className="border-b border-exito-100 bg-exito-50 px-5 py-2 text-xs font-medium text-exito-700" data-id="element-1545" data-mp="1gq7i79l5sj58">
              {aviso}
            </p>}

          {etapas.length === 0 && !nueva ? <EstadoVacio mensaje={`El flujo de ${materia} aún no tiene etapas`} sugerencia="Agrega la primera etapa o duplica el flujo de otra materia para partir de una base." data-id="element-1546" data-mp="1o9gr71pr6ftr" /> : <ol className="relative px-5 py-5" data-id="element-1547" data-mp="1y7wk9a8cbz5w">
              {etapas.map((etapa, i) => {
            const color = estiloColor(etapa.color);
            const editando = editandoId === etapa.id;
            return <li key={etapa.id} className="relative flex gap-4 pb-4 last:pb-0" data-id="element-1548" data-mp="2by02zr80haql" data-mpi={i}>
                    {i < etapas.length - 1 && <span className="absolute left-[15px] top-8 h-full w-0.5 bg-gris-200" aria-hidden="true" data-id="element-1549" data-mp="2gw1ik268arxb" />}
                    <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-2xs font-bold text-white ${color.punto}`} data-id="element-1550" data-mp="2pykjggqgeugx">
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1" data-id="element-1551" data-mp="kua4srkz60s8">
                      {editando ? <EditorEtapa etapa={etapa} orden={i} total={etapas.length} tiposDocumento={tiposParaMateria(materia).map(t => t.nombre)} onGuardar={(e, orden) => guardarEtapa(e, orden, false)} onCancelar={() => setEditandoId(null)} data-id="element-1552" data-mp="pkqmwozqqakx" /> : <TarjetaEtapa etapa={etapa} indice={i} arrastrando={arrastrando === i} destino={destino === i && arrastrando !== null && arrastrando !== i} onEditar={() => {
                  setEditandoId(etapa.id);
                  setNueva(null);
                }} onDuplicar={() => {
                  duplicarEtapa(materia, etapa.id);
                  setAviso(`Etapa «${etapa.nombre}» duplicada.`);
                }} onEliminar={() => {
                  eliminarEtapa(materia, etapa.id);
                  setAviso(`Etapa «${etapa.nombre}» eliminada del flujo ${materia}.`);
                }} onDragStart={() => setArrastrando(i)} onDragOver={() => setDestino(i)} onDrop={() => soltar(i)} onDragEnd={() => {
                  setArrastrando(null);
                  setDestino(null);
                }} data-id="element-1553" data-mp="18j2tq436j8dc" />}
                    </div>
                  </li>;
          })}

              {nueva && <li className="relative flex gap-4" data-id="element-1554" data-mp="rezw6nhrru9t">
                  <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-dashed border-marino-300 text-2xs font-bold text-marino-500" data-id="element-1555" data-mp="rdormlgi2ihn">
                    {etapas.length + 1}
                  </span>
                  <div className="min-w-0 flex-1" data-id="element-1556" data-mp="3pncuusqa5ts">
                    <EditorEtapa etapa={nueva} orden={etapas.length} total={etapas.length + 1} tiposDocumento={tiposParaMateria(materia).map(t => t.nombre)} onGuardar={(e, orden) => guardarEtapa(e, orden, true)} onCancelar={() => setNueva(null)} data-id="element-1557" data-mp="8h5g5pi84ihx" />
                  </div>
                </li>}
            </ol>}
        </Panel>
      </div>
    </>;
}